<template>
  <!-- When a Maps key is present, mount Google's PlaceAutocompleteElement here;
       otherwise fall back to a plain text input so the field always works. The
       Google element can't be seeded with an existing string value, so we show
       the saved city as a hint line beneath it. -->
  <div v-if="mapKey">
    <div ref="container" class="city-autocomplete" />
    <span v-if="modelValue" class="text-[11px] text-gray-400">Current: {{ modelValue }}</span>
  </div>
  <input
    v-else
    :value="modelValue"
    type="text"
    class="form-input w-full mt-1"
    @input="$emit('update:modelValue', $event.target.value)">
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { loadGoogleMaps } from '~/javascript/utils/google_maps'

const props = defineProps({
  mapKey: { type: String, default: '' },
  modelValue: { type: String, default: '' }
})

// update:modelValue → city text; select → { city, state } from the chosen place.
const emit = defineEmits(['update:modelValue', 'select'])

const container = ref(null)
let el = null

function componentValue(components, type, key = 'longText') {
  const c = (components || []).find((comp) => (comp.types || []).includes(type))
  return c ? (c[key] || c.long_name || c.short_name || '') : ''
}

async function handleSelect(event) {
  const prediction = event.placePrediction
  if (!prediction) return
  const place = prediction.toPlace()
  await place.fetchFields({ fields: ['addressComponents', 'displayName']})
  const components = place.addressComponents
  const city = componentValue(components, 'locality') ||
    componentValue(components, 'postal_town') ||
    place.displayName || ''
  const state = componentValue(components, 'administrative_area_level_1', 'shortText')
  emit('update:modelValue', city)
  emit('select', { city, state })
}

onMounted(async() => {
  if (!props.mapKey || !container.value) return
  try {
    const google = await loadGoogleMaps(props.mapKey)
    const { PlaceAutocompleteElement } = await google.maps.importLibrary('places')
    el = new PlaceAutocompleteElement({ types: ['(cities)']})
    el.setAttribute('placeholder', props.modelValue ? `${props.modelValue} — search to change` : 'Search for a city')
    container.value.appendChild(el)
    el.addEventListener('gmp-select', handleSelect)
  } catch (err) {
    console.error('City autocomplete failed to load:', err)
  }
})

onUnmounted(() => {
  if (el) {
    el.removeEventListener('gmp-select', handleSelect)
    el.parentNode?.removeChild(el)
    el = null
  }
})
</script>

<style>
.city-autocomplete { width: 100%; margin-top: 0.25rem; }
.city-autocomplete gmp-place-autocomplete { display: block; width: 100%; }
.city-autocomplete input {
  width: 100%;
  padding: 0.5rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
  color: #111827 !important;
  background-color: #fff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 0.375rem !important;
  box-sizing: border-box !important;
}
.city-autocomplete input:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 1px #3b82f6 !important;
}
</style>
